import { ReactElement, forwardRef, useId, useMemo } from 'react'

import { clsx } from 'clsx'
import { useInView } from 'react-intersection-observer'

import { useDebounce } from '../../common/hooks/useDebounce.js'
import { T } from '../../i18n/index.js'
import { useSubscribeToChat } from '../hooks/useSubscribeToChat.js'

import { TypingSvg } from './TypingSvg.js'

interface RendererProps {
  memberName: string
  remainingCount: number
  count: number
}
interface Props {
  chatId: string
  subscriptionDelay?: number
  children: (
    props: RendererProps,
    deferredProps: RendererProps,
    ref: (node?: Element | null) => void,
  ) => ReactElement
}

/**
 * This component is extracted into a HOC to prevent rerender and subsequent re-subscriptions
 * when presentational props such as class names change and stabilizes the subscriber Id.
 */
export const IsTypingIndicatorHoC = ({
  chatId,
  subscriptionDelay,
  children,
}: Props) => {
  const subscriberComponentId = useId()
  const { ref, inView } = useInView()
  const { typingMembers } = useSubscribeToChat({
    chatId,
    enabled: inView,
    subscriberId: subscriberComponentId,
    delay: subscriptionDelay,
  })

  const props = useMemo((): RendererProps => {
    const memberNames = typingMembers
      .slice(0, 2)
      .map(({ memberName }) => memberName)
      .filter(Boolean)
    const memberName = memberNames.at(0)
    const remainingCount = memberNames.length - 1

    return {
      memberName,
      count: memberNames.length,
      remainingCount,
    }
  }, [typingMembers])

  const deferredProps = useDebounce(props, 500)

  return children(props, deferredProps, ref)
}

export const IsTypingIndicatorRenderer = forwardRef(
  (
    props: {
      currentProps: RendererProps
      deferredProps: RendererProps
      className: string
    },
    ref: (node?: Element | null) => void,
  ) => {
    const { currentProps, deferredProps, className } = props

    const opacity = currentProps.count ? '1' : '0'

    const maxHeight = opacity === '0' ? '0px' : '100px'

    const isAboutToDisappear =
      currentProps.count === 0 && deferredProps.count > 0

    const memberName = isAboutToDisappear
      ? deferredProps.memberName
      : currentProps.memberName
    const remainingCount = isAboutToDisappear
      ? deferredProps.remainingCount
      : currentProps.remainingCount

    return (
      <div
        ref={ref}
        className={clsx('transition-all duration-300', className)}
        style={{
          opacity,
          maxHeight,
        }}
      >
        <div className="flex items-center">
          <div className="text-interactive inline-block truncate flex items-center min-w-0">
            <T
              id="Messaging.Chat.IsTypingIndicator"
              defaultMessage="{memberName} {remainingCount, plural, =0 {is typing} =1 {and 1 other are typing} other {and {remainingCount} others are typing}}"
              values={{
                memberName,
                remainingCount,
              }}
            />
          </div>
          <TypingSvg className="text-interactive shrink-0" />
        </div>
      </div>
    )
  },
)
